import React, { useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, MessageCircle } from 'lucide-react';
import { useLanguage } from '../hooks/useLanguage';
interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function ContactModal({
  isOpen,
  onClose
}: ContactModalProps) {
  const {
    language
  } = useLanguage();
  const [showSuccess, setShowSuccess] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
      // Prevent iOS zoom on input focus
      const viewport = document.querySelector('meta[name=viewport]');
      if (viewport) {
        viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
      }
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
      // Restore normal viewport
      const viewport = document.querySelector('meta[name=viewport]');
      if (viewport) {
        viewport.setAttribute('content', 'width=device-width, initial-scale=1.0');
      }
    };
  }, [isOpen, onClose]);
  // Countdown timer for auto-close
  useEffect(() => {
    if (showSuccess && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (showSuccess && countdown === 0) {
      handleClose();
    }
  }, [showSuccess, countdown]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setShowSuccess(true);
    setCountdown(5);
  };
  const handleClose = () => {
    setShowSuccess(false);
    setCountdown(5);
    setFormData({
      name: '',
      email: '',
      subject: '',
      message: ''
    });
    onClose();
  };
  const handleWhatsApp = () => {
    const phoneNumber = '64212231312'; // +64 21 223 1312 without spaces/symbols
    const message = language === 'fr' ? "Bonjour, je souhaite obtenir plus d'informations sur vos services." : 'Hello, I would like to get more information about your services.';
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };
  const accentColor = '#ff00ff'; // Magenta - matches secondary CTA
  return <AnimatePresence data-id="element-27">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-lg overflow-hidden pt-16 sm:pt-0" onClick={handleClose} data-id="element-28">
          <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.95
      }} transition={{
        duration: 0.2
      }} onClick={e => e.stopPropagation()} className="relative w-full h-full sm:h-auto sm:max-w-2xl bg-dark-card border border-white/10 p-4 sm:p-8 md:p-12 overflow-y-auto sm:max-h-[90vh]" style={{
        boxShadow: `0 0 60px ${accentColor}40, 0 0 100px ${accentColor}20, inset 0 0 40px ${accentColor}08`
      }} data-id="element-29">
            {/* Corner Accents - Top Right & Bottom Left ONLY with enhanced glow */}
            <div className="absolute top-0 right-0 w-6 h-6 sm:w-8 sm:h-8 pointer-events-none" data-id="element-30">
              <div className="absolute top-0 right-0 w-full h-0.5" style={{
            background: accentColor,
            boxShadow: `0 0 10px ${accentColor}, 0 0 20px ${accentColor}80`
          }} data-id="element-31" />
              <div className="absolute top-0 right-0 w-0.5 h-full" style={{
            background: accentColor,
            boxShadow: `0 0 10px ${accentColor}, 0 0 20px ${accentColor}80`
          }} data-id="element-32" />
            </div>
            <div className="absolute bottom-0 left-0 w-6 h-6 sm:w-8 sm:h-8 pointer-events-none" data-id="element-33">
              <div className="absolute bottom-0 left-0 w-full h-0.5" style={{
            background: accentColor,
            boxShadow: `0 0 10px ${accentColor}, 0 0 20px ${accentColor}80`
          }} data-id="element-34" />
              <div className="absolute bottom-0 left-0 w-0.5 h-full" style={{
            background: accentColor,
            boxShadow: `0 0 10px ${accentColor}, 0 0 20px ${accentColor}80`
          }} data-id="element-35" />
            </div>

            {/* Close Button */}
            <button onClick={handleClose} className="absolute top-2 right-2 sm:top-4 sm:right-4 text-gray-500 hover:text-magenta transition-colors z-10" aria-label="Close" data-id="element-36">
              <X size={20} className="sm:w-6 sm:h-6" data-id="element-37" />
            </button>

            {!showSuccess ? <div className="h-full flex flex-col" data-id="element-38">
                <h2 className="font-cyber text-xl sm:text-2xl md:text-3xl font-bold text-white mb-1 sm:mb-2" data-id="element-39">
                  {language === 'fr' ? 'NOUS CONTACTER' : 'CONTACT US'}
                </h2>
                <p className="text-gray-400 text-xs sm:text-sm mb-4 sm:mb-6" data-id="element-40">
                  {language === 'fr' ? "Une question ? Besoin d'informations ? Nous sommes là pour vous aider." : "Have a question? Need information? We're here to help."}
                </p>

                {/* WhatsApp Button */}
                <button onClick={handleWhatsApp} className="w-full mb-4 sm:mb-6 relative group px-4 py-3 sm:px-6 sm:py-4 font-cyber font-bold text-xs sm:text-sm tracking-widest uppercase transition-all duration-300 overflow-hidden bg-[#25D366]/10 hover:bg-[#25D366]/20 border border-[#25D366]/30 hover:border-[#25D366] hover:shadow-[0_0_30px_rgba(37,211,102,0.3)] flex items-center justify-center gap-3" style={{
            clipPath: 'polygon(12px 0, 100% 0, 100% calc(100% - 12px), calc(100% - 12px) 100%, 0 100%, 0 12px)'
          }} data-id="element-41">
                  <MessageCircle size={18} className="text-[#25D366]" data-id="element-42" />
                  <span className="text-[#25D366] group-hover:text-white transition-colors" data-id="element-43">
                    {language === 'fr' ? 'CONTACTER VIA WHATSAPP' : 'CONTACT VIA WHATSAPP'}
                  </span>
                </button>

                {/* Divider */}
                <div className="flex items-center gap-4 mb-4 sm:mb-6" data-id="element-44">
                  <div className="flex-1 h-px bg-white/10" data-id="element-45"></div>
                  <span className="text-xs text-gray-500 font-cyber uppercase tracking-widest" data-id="element-46">
                    {language === 'fr' ? 'Ou' : 'Or'}
                  </span>
                  <div className="flex-1 h-px bg-white/10" data-id="element-47"></div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-6 flex-1 flex flex-col" data-id="element-48">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6" data-id="element-49">
                    <div data-id="element-50">
                      <label className="block text-[10px] sm:text-xs font-cyber text-gray-400 mb-1 sm:mb-2 uppercase tracking-wider" data-id="element-51">
                        {language === 'fr' ? 'Nom' : 'Name'} *
                      </label>
                      <input type="text" required value={formData.name} onChange={e => setFormData({
                  ...formData,
                  name: e.target.value
                })} className="w-full bg-black/50 border border-white/10 px-3 py-2 sm:px-4 sm:py-3 text-white text-base focus:border-magenta focus:outline-none transition-colors" style={{
                  clipPath: 'polygon(6px 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%, 0 6px)',
                  fontSize: '16px'
                }} data-id="element-52" />
                    </div>
                    <div data-id="element-53">
                      <label className="block text-[10px] sm:text-xs font-cyber text-gray-400 mb-1 sm:mb-2 uppercase tracking-wider" data-id="element-54">
                        Email *
                      </label>
                      <input type="email" required value={formData.email} onChange={e => setFormData({
                  ...formData,
                  email: e.target.value
                })} className="w-full bg-black/50 border border-white/10 px-3 py-2 sm:px-4 sm:py-3 text-white text-base focus:border-magenta focus:outline-none transition-colors" style={{
                  clipPath: 'polygon(6px 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%, 0 6px)',
                  fontSize: '16px'
                }} data-id="element-55" />
                    </div>
                  </div>

                  <div data-id="element-56">
                    <label className="block text-[10px] sm:text-xs font-cyber text-gray-400 mb-1 sm:mb-2 uppercase tracking-wider" data-id="element-57">
                      {language === 'fr' ? 'Sujet' : 'Subject'} *
                    </label>
                    <input type="text" required value={formData.subject} onChange={e => setFormData({
                ...formData,
                subject: e.target.value
              })} className="w-full bg-black/50 border border-white/10 px-3 py-2 sm:px-4 sm:py-3 text-white text-base focus:border-magenta focus:outline-none transition-colors" style={{
                clipPath: 'polygon(6px 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%, 0 6px)',
                fontSize: '16px'
              }} data-id="element-58" />
                  </div>

                  <div className="flex-1" data-id="element-59">
                    <label className="block text-[10px] sm:text-xs font-cyber text-gray-400 mb-1 sm:mb-2 uppercase tracking-wider" data-id="element-60">
                      Message *
                    </label>
                    <textarea required rows={3} value={formData.message} onChange={e => setFormData({
                ...formData,
                message: e.target.value
              })} className="w-full bg-black/50 border border-white/10 px-3 py-2 sm:px-4 sm:py-3 text-white text-base focus:border-magenta focus:outline-none transition-colors resize-none" style={{
                clipPath: 'polygon(6px 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%, 0 6px)',
                fontSize: '16px'
              }} data-id="element-61" />
                  </div>

                  <button type="submit" className="w-full relative group px-6 py-3 sm:px-8 sm:py-4 font-cyber font-bold text-xs sm:text-sm tracking-widest uppercase transition-all duration-300 overflow-hidden text-magenta hover:text-white hover:bg-magenta/20 hover:shadow-[0_0_30px_rgba(255,0,255,0.5)]" style={{
              clipPath: 'polygon(15px 0, 100% 0, 100% calc(100% - 15px), calc(100% - 15px) 100%, 0 100%, 0 15px)'
            }} data-id="element-62">
                    <div className="absolute inset-0 border border-magenta bg-transparent transition-all duration-300 group-hover:border-magenta" style={{
                clipPath: 'polygon(15px 0, 100% 0, 100% calc(100% - 15px), calc(100% - 15px) 100%, 0 100%, 0 15px)'
              }} data-id="element-63" />
                    <span className="relative z-10" data-id="element-64">
                      {language === 'fr' ? 'ENVOYER' : 'SEND'}
                    </span>
                  </button>
                </form>
              </div> : <SuccessAnimation accentColor={accentColor} title={language === 'fr' ? 'Message envoyé avec succès' : 'Message sent successfully'} subtitle={language === 'fr' ? 'Votre message a bien été envoyé. Nous vous répondrons rapidement.' : 'Your message has been sent. We will respond quickly.'} countdown={countdown} language={language} data-id="element-65" />}
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
function SuccessAnimation({
  accentColor,
  title,
  subtitle,
  countdown,
  language
}: {
  accentColor: string;
  title: string;
  subtitle: string;
  countdown: number;
  language: string;
}) {
  return <div className="flex flex-col items-center justify-center py-8 sm:py-12 h-full" data-id="element-66">
      <motion.div className="relative w-24 h-24 sm:w-32 sm:h-32 mb-6 sm:mb-8" data-id="element-67">
        {/* Data Flow Lines */}
        <motion.div initial={{
        pathLength: 0,
        opacity: 0
      }} animate={{
        pathLength: 1,
        opacity: [0, 1, 0]
      }} transition={{
        duration: 0.4,
        ease: 'linear'
      }} className="absolute inset-0" data-id="element-68">
          <svg className="w-full h-full" viewBox="0 0 100 100" data-id="element-69">
            <line x1="0" y1="50" x2="50" y2="50" stroke={accentColor} strokeWidth="1" opacity="0.6" data-id="element-70" />
            <line x1="100" y1="50" x2="50" y2="50" stroke={accentColor} strokeWidth="1" opacity="0.6" data-id="element-71" />
            <line x1="50" y1="0" x2="50" y2="50" stroke={accentColor} strokeWidth="1" opacity="0.6" data-id="element-72" />
            <line x1="50" y1="100" x2="50" y2="50" stroke={accentColor} strokeWidth="1" opacity="0.6" data-id="element-73" />
          </svg>
        </motion.div>

        {/* Circle */}
        <motion.svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 100 100" data-id="element-74">
          <motion.circle cx="50" cy="50" r="35" fill="none" stroke={accentColor} strokeWidth="2" initial={{
          pathLength: 0
        }} animate={{
          pathLength: 1
        }} transition={{
          duration: 0.4,
          delay: 0.4,
          ease: 'easeInOut'
        }} style={{
          filter: `drop-shadow(0 0 4px ${accentColor}80)`
        }} data-id="element-75" />
        </motion.svg>

        {/* Checkmark */}
        <motion.svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.3,
        delay: 0.8
      }} data-id="element-76">
          <motion.path d="M 30 50 L 45 65 L 70 35" fill="none" stroke={accentColor} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" initial={{
          pathLength: 0
        }} animate={{
          pathLength: 1
        }} transition={{
          duration: 0.3,
          delay: 0.8,
          ease: 'easeOut'
        }} style={{
          filter: `drop-shadow(0 0 4px ${accentColor}80)`
        }} data-id="element-77" />
        </motion.svg>

        {/* Glow Pulse */}
        <motion.div className="absolute inset-0 rounded-full" initial={{
        opacity: 0
      }} animate={{
        opacity: [0, 0.3, 0]
      }} transition={{
        duration: 0.6,
        delay: 1.1
      }} style={{
        boxShadow: `0 0 30px ${accentColor}`
      }} data-id="element-78" />
      </motion.div>

      <motion.h3 initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 1.2
    }} className="font-cyber text-lg sm:text-2xl font-bold text-white mb-2 sm:mb-3 text-center px-4" tabIndex={0} data-id="element-79">
        {title}
      </motion.h3>
      <motion.p initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 1.3
    }} className="text-gray-400 text-center text-sm sm:text-base max-w-md px-4 mb-6" data-id="element-80">
        {subtitle}
      </motion.p>

      {/* Countdown */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 1.4
    }} className="flex items-center gap-2 text-xs font-cyber text-gray-500 uppercase tracking-widest" data-id="element-81">
        <span data-id="element-82">
          {language === 'fr' ? 'Fermeture automatique dans' : 'Auto-closing in'}
        </span>
        <span className="text-magenta font-bold text-base" data-id="element-83">{countdown}s</span>
      </motion.div>
    </div>;
}